import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import BaseInput from "../BaseInput";
import TextInput from "./TextInput";
import SwitchInput from "./SwitchInput";
import Popper from "@fragments/Popper";
import Config from "@config";
import { LocationOnRounded, SearchRounded } from "@mui/icons-material";
import "./AddressInput.scss";

// ── helpers ──────────────────────────────────────────────────────────────────

function debounce(func, wait) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
}

function loadGoogleMaps(apiKey) {
    if (document.querySelector("#google-maps")) return;
    const script = document.createElement("script");
    script.setAttribute("async", "");
    script.setAttribute("id", "google-maps");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    document.head.appendChild(script);
}

function parseAddressComponents(components, prediction) {
    const address = {
        description: prediction.description,
        place_id: prediction.place_id,
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
        zipcode: "",
    };
    components.forEach((c) => {
        const t = c.types;
        if (t.includes("route")) address.street = c.long_name;
        if (t.includes("street_number")) address.number = c.long_name;
        if (
            t.includes("sublocality") ||
            t.includes("sublocality_level_1") ||
            t.includes("sublocality_level_2")
        )
            address.neighborhood = c.long_name;
        if (t.includes("administrative_area_level_2") || t.includes("locality"))
            address.city = c.long_name;
        if (t.includes("administrative_area_level_1")) address.state = c.short_name;
        if (t.includes("postal_code")) address.zipcode = c.long_name.replace("-", "");
    });
    return address;
}

// ── component ─────────────────────────────────────────────────────────────────

const AddressInput = ({
    value,
    onInput,
    label,
    placeholder,
    className,
    ...props
}) => {
    // Defaults are resolved here rather than in the parameter list so they
    // follow the active language instead of freezing at module evaluation.
    const { t } = useTranslation("fragments");
    const resolvedLabel = label ?? t("input.address.label");
    const resolvedPlaceholder = placeholder ?? t("input.address.placeholder");

    const [searchText, setSearchText] = useState(value?.description || "");
    const [localAddress, setLocalAddress] = useState(value || null);
    const [showComplement, setShowComplement] = useState(false);

    const popperRef = useRef(null);
    const searchWrapperRef = useRef(null);
    const complementInputRef = useRef(null);
    const autocompleteService = useRef(null);
    const placesService = useRef(null);
    const scriptLoaded = useRef(false);

    // Sync controlled value
    useEffect(() => {
        if (value === undefined) return;
        setLocalAddress(value);
        if (value?.description) setSearchText(value.description);
        else if (!value) setSearchText("");
    }, [value]);

    // Load Google Maps script once
    useEffect(() => {
        if (typeof window === "undefined" || scriptLoaded.current) return;
        loadGoogleMaps(Config.googlePlacesApiKey);
        scriptLoaded.current = true;
    }, []);

    useEffect(() => {
        if (showComplement && complementInputRef.current) {
            setTimeout(() => complementInputRef.current?.focus(), 50);
        }
    }, [showComplement]);

    const fetchPredictions = useMemo(
        () =>
            debounce((input, callback) => {
                if (!autocompleteService.current && window.google?.maps?.places) {
                    autocompleteService.current =
                        new window.google.maps.places.AutocompleteService();
                }
                autocompleteService.current?.getPlacePredictions(
                    { input, componentRestrictions: { country: "br" } },
                    callback
                );
            }, 400),
        []
    );

    // ── resolve a selected place ──────────────────────────────────────────────

    function resolvePlace(prediction) {
        if (!placesService.current && window.google?.maps?.places) {
            placesService.current = new window.google.maps.places.PlacesService(
                document.createElement("div")
            );
        }
        if (!placesService.current) return;

        placesService.current.getDetails(
            { placeId: prediction.place_id, fields: ["address_components"] },
            (place, status) => {
                if (status !== window.google.maps.places.PlacesServiceStatus.OK) return;
                const address = parseAddressComponents(
                    place.address_components,
                    prediction
                );
                setLocalAddress(address);
                onInput(address);
            }
        );
    }

    // ── handlers ─────────────────────────────────────────────────────────────

    function handleSearchChange(text) {
        setSearchText(text);

        // Reset sub-fields when search text changes
        if (localAddress) {
            setLocalAddress(null);
            onInput(null);
        }

        if (!text.trim()) {
            popperRef.current?.close();
            return;
        }

        fetchPredictions(text, (results) => {
            const predictions = results || [];

            if (!predictions.length || !searchWrapperRef.current) {
                popperRef.current?.close();
                return;
            }

            const options = predictions.map((p) => ({
                id: p.place_id,
                label: p.description,
                icon: <LocationOnRounded style={{ fontSize: 16, opacity: 0.6 }} />,
                onClick: () => {
                    setSearchText(p.description);
                    resolvePlace(p);
                },
            }));

            popperRef.current?.open(
                options,
                "bottom-start",
                searchWrapperRef.current
            );
        });
    }

    function updateField(field, val) {
        const updated = { ...localAddress, [field]: val };
        setLocalAddress(updated);
        onInput(updated);
    }

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <Popper
            controlRef={popperRef}
            config={{ disableFlip: true }}
            paperSx={{
                minWidth: searchWrapperRef.current?.offsetWidth ?? 240,
                maxWidth: "min(560px, calc(100vw - 32px))",
            }}
        >
            <div className={`address-input-root ${className || ""}`}>
                {/* Search field */}
                <div ref={searchWrapperRef}>
                    <BaseInput
                        label={resolvedLabel}
                        placeholder={resolvedPlaceholder}
                        value={searchText}
                        onInput={handleSearchChange}
                        start={<LocationOnRounded />}
                        end={<SearchRounded />}
                        autoComplete="off"
                        {...props}
                    />
                </div>

                {/* Sub-fields — filled once a place is resolved */}
                {localAddress && (
                    <div className="address-sub-fields">
                        <div className="address-row">
                            <TextInput
                                label={t("input.address.state")}
                                value={localAddress.state || ""}
                                onInput={(v) => updateField("state", v)}
                            />
                            <TextInput
                                label={t("input.address.city")}
                                value={localAddress.city || ""}
                                onInput={(v) => updateField("city", v)}
                            />
                        </div>
                        <div className="address-row">
                            <TextInput
                                label={t("input.address.neighborhood")}
                                value={localAddress.neighborhood || ""}
                                onInput={(v) => updateField("neighborhood", v)}
                            />
                            <TextInput
                                label={t("input.address.zipcode")}
                                value={localAddress.zipcode || ""}
                                onInput={(v) => updateField("zipcode", v)}
                            />
                        </div>
                        <div className="address-row">
                            <TextInput
                                label={t("input.address.street")}
                                value={localAddress.street || ""}
                                onInput={(v) => updateField("street", v)}
                                style={{ flex: 1 }}
                            />
                            <TextInput
                                label={t("input.address.number")}
                                value={localAddress.number || ""}
                                onInput={(v) => updateField("number", v)}
                                placeholder={t("input.address.numberPlaceholder")}
                                style={{ width: "100px", flexShrink: 0 }}
                            />
                        </div>
                        <SwitchInput
                            label={t("input.address.addComplement")}
                            value={showComplement}
                            onInput={setShowComplement}
                        />
                        {showComplement && (
                            <TextInput
                                ref={complementInputRef}
                                label={t("input.address.complement")}
                                value={localAddress.complement || ""}
                                onInput={(v) => updateField("complement", v)}
                                placeholder={t("input.address.complementPlaceholder")}
                            />
                        )}
                    </div>
                )}
            </div>
        </Popper>
    );
};

export default AddressInput;
